"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthRoutes = exports.AuthLogoutReplyV1 = exports.AuthLogoutQueryV1 = exports.AuthRenewReplyV1 = exports.AuthSessionClosedV1 = exports.AuthRenewErrorV1 = exports.AuthRenewQueryV1 = exports.AuthTenantHintReplyV1 = exports.AuthTenantHintQueryV1 = exports.AuthLoginReplyV1 = exports.AuthLoginQueryV1 = void 0;
const zod_1 = require("zod");
const message_1 = require("./message");
const subject_contract_1 = require("./subject-contract");
exports.AuthLoginQueryV1 = message_1.QueryMessage.extend({
    type: zod_1.z.literal('AuthLoginQuery'),
    version: zod_1.z.literal(1),
    username: zod_1.z.string().min(1),
    password: zod_1.z.string().min(1),
    loginTenantId: zod_1.z.string().min(1),
    /** IP клиента (из HTTP-запроса) — для журнала login_failed (эпик TOKREG). */
    ip: zod_1.z.string().optional(),
});
exports.AuthLoginReplyV1 = zod_1.z.discriminatedUnion('isOk', [
    zod_1.z.object({
        isOk: zod_1.z.literal(true),
        token: zod_1.z.string(),
        /**
         * Через сколько секунд после получения токена вкладке пора его продлять (SESSRENEW O3):
         * порог живёт в конфиге ядра, UI его не хардкодит. Optional — аддитивно к старым клиентам.
         */
        renewAfterSec: zod_1.z.number().int().positive().optional(),
    }),
    zod_1.z.object({
        isOk: zod_1.z.literal(false),
        error: zod_1.z.string(),
    }),
]);
/**
 * Подсказка тенанта для страницы входа по её хосту (эпик CUSTDOM): `callc.cardio-tomsk.ru` →
 * `cardio`. Точный лукап одного хоста среди подтверждённых своих веб-доменов активных тенантов —
 * списка тенантов до входа не отдаётся никогда. Неизвестный, кривой и неактивный хост отвечают
 * одинаково (`tenantId: null`), без имени и цвета. Идёт REST (`GET /api/v1/auth/tenant-hint`),
 * у браузера до входа шины нет.
 */
exports.AuthTenantHintQueryV1 = message_1.QueryMessage.extend({
    type: zod_1.z.literal('AuthTenantHintQuery'),
    version: zod_1.z.literal(1),
    /** `location.hostname` страницы входа; нормализуется на стороне ядра. */
    host: zod_1.z.string().max(253),
});
exports.AuthTenantHintReplyV1 = zod_1.z.object({
    tenantId: zod_1.z.string().nullable(),
});
/**
 * Продление логин-сессии без перелогина (эпик SESSRENEW). Приходит из REST
 * `POST /api/v1/auth/renew` (Bearer = текущий токен): контроллер кладёт сюда сам токен.
 * Ядро проверяет его подпись/срок/issuer/audience и берёт `jti`/`sub`/`tenantId`/`iat` только из
 * проверенных claims (R13): к шине есть доступ не только у REST, и личность из тела запроса
 * позволила бы продлить чужую сессию. `jti` у сессии не меняется (O2), `iat` предъявленной
 * копии нужен ядру для правила «продлевает только последняя копия» (O11).
 */
exports.AuthRenewQueryV1 = message_1.QueryMessage.extend({
    type: zod_1.z.literal('AuthRenewQuery'),
    version: zod_1.z.literal(1),
    /** Продлеваемый логин-JWT целиком. */
    token: zod_1.z.string().min(1),
    /** IP клиента — для журнала. */
    ip: zod_1.z.string().optional(),
});
/** Терминальные причины отказа продления (UI показывает модалку перелогина, O9). */
exports.AuthRenewErrorV1 = zod_1.z.enum([
    /** Токен не прошёл проверку (подпись, срок, issuer/audience, формат claims) или его тенант — `'*'`. */
    'invalid_token',
    'no_jti',
    'not_found',
    'not_user_token',
    'revoked',
    'expired',
    'stale_copy',
    'user_inactive',
    'tenant_inactive',
]);
/**
 * Почему сервер закрыл сессию — для текста модалки перелогина (SESSRENEW O13). Есть только у
 * отказов `stale_copy` и `revoked`, и только для причин, которые платформа ставит сама:
 * - `copy_reuse` — предъявлена устаревшая копия токена: у сессии два владельца (копия украдена или
 *   ответ продления потерялся), сервер отозвал сессию целиком;
 * - `logout_all_devices` — пользователь вышел на всех устройствах.
 * Отзыв админом и прочие причины сюда не попадают — модалка показывает общий текст.
 */
exports.AuthSessionClosedV1 = zod_1.z.object({
    reason: zod_1.z.enum(['copy_reuse', 'logout_all_devices']),
    /** Последняя выданная копия — только у `copy_reuse`. */
    lastCopy: zod_1.z
        .object({
        /** ISO-8601: когда выдана (вход или продление). */
        at: zod_1.z.string(),
        /**
         * Адрес, с которого её получили, — только если он известен и отличается от адреса этого
         * запроса. `null` — тот же адрес или сравнить не с чем: модалка пишет нейтральный текст.
         */
        otherIp: zod_1.z.string().nullable(),
    })
        .optional(),
});
exports.AuthRenewReplyV1 = zod_1.z.discriminatedUnion('isOk', [
    zod_1.z.object({
        isOk: zod_1.z.literal(true),
        token: zod_1.z.string(),
        /** ISO-8601 нового `exp`. */
        expiresAt: zod_1.z.string(),
        renewAfterSec: zod_1.z.number().int().positive(),
    }),
    zod_1.z.object({
        isOk: zod_1.z.literal(false),
        error: exports.AuthRenewErrorV1,
        closed: exports.AuthSessionClosedV1.optional(),
    }),
]);
/**
 * «Выход» с отзывом сессии на сервере: с O2 это одна строка — отзыв `jti`. Как и renew, несёт
 * сам токен (R13): отзывается только сессия из проверенных claims и только своего `sub`.
 */
exports.AuthLogoutQueryV1 = message_1.QueryMessage.extend({
    type: zod_1.z.literal('AuthLogoutQuery'),
    version: zod_1.z.literal(1),
    /** Логин-JWT завершаемой сессии. */
    token: zod_1.z.string().min(1),
    /** IP клиента — для журнала. */
    ip: zod_1.z.string().optional(),
    /**
     * «Выйти на всех устройствах» (SESSRENEW O13): отозвать все логин-сессии этого пользователя в
     * его тенанте, а не только предъявленную. Сервисные токены не затрагиваются.
     */
    allDevices: zod_1.z.boolean().optional(),
});
exports.AuthLogoutReplyV1 = zod_1.z.object({
    /**
     * false — отзывать было нечего (нет jti / уже отозван / не найден / токен не прошёл проверку);
     * для клиента выход всё равно успешен.
     */
    revoked: zod_1.z.boolean(),
    /** Сколько сессий отозвано (с `allDevices` — все устройства пользователя). */
    revokedCount: zod_1.z.number().int().nonnegative().optional(),
});
exports.AuthRoutes = {
    login: (0, subject_contract_1.defineQuery)('q.auth.login', exports.AuthLoginQueryV1, exports.AuthLoginReplyV1),
    renew: (0, subject_contract_1.defineQuery)('q.auth.renew', exports.AuthRenewQueryV1, exports.AuthRenewReplyV1),
    logout: (0, subject_contract_1.defineQuery)('q.auth.logout', exports.AuthLogoutQueryV1, exports.AuthLogoutReplyV1),
    tenantHint: (0, subject_contract_1.defineQuery)('q.auth.tenant_hint', exports.AuthTenantHintQueryV1, exports.AuthTenantHintReplyV1),
};
//# sourceMappingURL=auth.js.map